<template>
  <section class="cart" v-if="sortedOrderLines">
    <aside class="cart__edge cart__edge--top"></aside>
    <HUICollapse
      ref="youCollapse"
      expanded
      class="cart__collapse"
      v-if="sortedOrderLines.you && sortedOrderLines.you.orderLines.length > 0"
    >
      <span slot="label" class="cart__item-label">
        <span>Din bestilling</span>
        <span>
          <span class="cart__item-label-expand">Vis</span>
          <span class="cart__item-label-collapse">Skjul</span>
          <HUIIconArrowLeft class="cart__item-label-icon"></HUIIconArrowLeft>
        </span>
      </span>
      <transition-group name="cart__item-" tag="ul">
        <li
          class="cart__item"
          v-for="orderLine in sortedOrderLines.you.orderLines"
          :key="orderLine.token"
        >
          <HUICartBurger
            v-if="orderLine.type === 'burger'"
            :disabled="disabled"
            :standard-config="getBurgerStandardConfig(orderLine)"
            :order-line="orderLine"
            :buns="menu.buns"
            :patties="menu.patties"
            :fryings="menu.fryings"
            :ingredients="menu.ingredients"
            :sent-to-kitchen="!!orderLine.prepared_at"
            :paid="!!orderLine.paid_at"
            :ready="markReadyOrderLines ? orderLine.is_ready : false"
            :storage-origin="storageOrigin"
            @click="handleCartBurgerClick"
          ></HUICartBurger>
          <HUICartProduct
            v-else
            :disabled="disabled"
            :order-line="orderLine"
            :name="getName(orderLine.product_id)"
            :sent-to-kitchen="!!orderLine.prepared_at"
            :paid="!!orderLine.paid_at"
            :ready="markReadyOrderLines ? orderLine.is_ready : false"
            @change="handleCartProductChange"
          ></HUICartProduct>
        </li>
      </transition-group>
    </HUICollapse>
    <HUICollapse
      ref="othersCollapse"
      :expanded="expandOthers"
      class="cart__collapse"
      v-for="key in otherKeys"
      :key="key"
    >
      <span slot="label" class="cart__item-label">
        <span>
          <template v-if="sortedOrderLines[key].customer_name"
            >{{ getNameInGenitive(sortedOrderLines[key].customer_name) }} bestilling</template
          >
          <template v-else-if="sortedOrderLines[key].customer_token">
            Bestilling fra anden gæst
          </template>
          <template v-else
            >Andre bestillinger</template
          >
        </span>
        <span>
          <span class="cart__item-label-expand">Vis</span>
          <span class="cart__item-label-collapse">Skjul</span>
          <HUIIconArrowLeft class="cart__item-label-icon"></HUIIconArrowLeft>
        </span>
      </span>
      <transition-group name="cart__item-" tag="ul">
        <li
          class="cart__item"
          v-for="orderLine in sortedOrderLines[key].orderLines"
          :key="orderLine.token"
        >
          <HUICartBurger
            v-if="orderLine.type === 'burger'"
            :disabled="shouldBeDisabled"
            :standard-config="getBurgerStandardConfig(orderLine)"
            :order-line="orderLine"
            :buns="menu.buns"
            :patties="menu.patties"
            :fryings="menu.fryings"
            :ingredients="menu.ingredients"
            :sent-to-kitchen="!!orderLine.prepared_at"
            :paid="!!orderLine.paid_at"
            :ready="markReadyOrderLines ? orderLine.is_ready : false"
            :storage-origin="storageOrigin"
            @click="handleCartBurgerClick"
          ></HUICartBurger>
          <HUICartProduct
            v-else
            :disabled="shouldBeDisabled"
            :order-line="orderLine"
            :name="getName(orderLine.product_id)"
            :sent-to-kitchen="!!orderLine.prepared_at"
            :paid="!!orderLine.paid_at"
            :ready="markReadyOrderLines ? orderLine.is_ready : false"
            @change="handleCartProductChange"
          ></HUICartProduct>
        </li>
      </transition-group>
    </HUICollapse>
    <footer class="cart__footer">
      <template v-if="paidAmount === totalPrice">
        <span>Regningen er betalt</span>
        Total: {{ formatPrice(totalPrice) }} kr
      </template>
      <template v-else-if="paidAmount > totalPrice">
        <span>{{ formatPrice(paidAmount - totalPrice) }} kr til gode &ndash; Kontakt tjeneren</span>
        Total: {{ formatPrice(totalPrice) }} kr
      </template>
      <template v-else-if="paidAmount > 0">
        <span></span>
        Betalt: {{ formatPrice(paidAmount) }} kr / {{ formatPrice(totalPrice) }} kr
      </template>
      <template v-else>
        <span></span>
        Total: {{ formatPrice(totalPrice) }} kr
      </template>
    </footer>
    <aside class="cart__edge cart__edge--bottom"></aside>
    <ModalVC
      ref="burger-modal-modal"
      v-if="currentBurgerModalStandardConfig"
      :color="currentBurgerModalStandardConfig.external_color"
      :heading="currentBurgerModalStandardConfig.name"
      @close="handleBurgerModalClosed"
    >
      <HUIBurgerModal
        @update="handleBurgerModalUpdate"
        @delete="handleBurgerModalDelete"
        :standard-config="currentBurgerModalStandardConfig"
        :edit-config="currentBurgerModalEditConfig"
        :all-buns="menu.buns"
        :all-patties="menu.patties"
        :all-ingredients="menu.ingredients"
        :all-fryings="menu.fryings"
        :is-lunch="order.is_lunch"
        :storage-origin="storageOrigin"
        :counter-max="1"
        :offline="offline"
      ></HUIBurgerModal>
    </ModalVC>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import {
  HUICollapse,
  HUIIconArrowLeft,
  HUIBurgerModal,
  HUICartBurger,
  HUICartProduct
} from '@/halifax-ui'
import ModalVC from '@/containers/ModalVC'
import formatPrice from '@/utils/formatPrice'
import { ORIGIN } from '@/api'
import { OrderStatus } from '@/store/order'

export default {
  components: {
    HUICartBurger,
    HUICartProduct,
    HUICollapse,
    HUIIconArrowLeft,
    ModalVC,
    HUIBurgerModal
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    menu: {
      type: Object,
      required: true
    },
    sortedOrderLines: {
      type: Object
    },
    markReadyOrderLines: Boolean,
    disabled: Boolean,
    expandOthers: Boolean
  },
  data() {
    return {
      currentBurgerModalStandardConfig: null,
      currentBurgerModalEditConfig: null,
      storageOrigin: ORIGIN
    }
  },
  computed: {
    ...mapState({
      offline: 'offline'
    }),
    ...mapState('user', {
      userClientToken: 'client_token'
    }),
    otherKeys() {
      return Object.keys(this.sortedOrderLines).filter(key => key !== 'you')
    },
    orderLines() {
      return this.order.order_lines
    },
    shouldBeDisabled() {
      return this.disabled || this.order.status === OrderStatus.CLOSED
    },
    totalPrice() {
      return this.orderLines.reduce((totalPrice, orderLine) => {
        totalPrice += orderLine.price
        return totalPrice
      }, 0)
    },
    paidAmount() {
      return (this.order.payments || []).reduce((totalPrice, payment) => {
        totalPrice += payment.price
        return totalPrice
      }, 0)
    }
  },
  watch: {
    sortedOrderLines() {
      // Wait for the DOM to update
      this.$nextTick(() => {
        if (this.$refs.youCollapse) {
          this.$refs.youCollapse.refresh()
        }
        if (this.$refs.othersCollapse) {
          this.$refs.othersCollapse.forEach(huiCollapseInstance => huiCollapseInstance.refresh())
        }
      })
    }
  },
  methods: {
    handleCartBurgerClick(payload) {
      const config = {
        id: payload.burger_id,
        quantity: 1,
        bun: payload.bun_id,
        patty: payload.patty_id,
        frying: payload.frying_id,
        ingredients: payload.ingredients,
        orderLineToken: payload.token
      }
      this.openBurgerModal(config)
    },
    getNameInGenitive(name) {
      return ['s', 'x'].includes(name.substr(-1)) ? `${name}'` : `${name}s`
    },
    getBurgerStandardConfig(orderLine) {
      const id = orderLine.burger_id

      return (
        this.menu.burgers.find(burger => burger.id === id) ||
        this.menu.burger_bowls.find(burgerBowl => burgerBowl.id === id)
      )
    },
    getName(productId) {
      const index = this.menu.products_index[productId]
      const product = this.menu.products[index]
      return product.name
    },
    handleCartProductChange(payload) {
      this.$emit('product-change', payload)
    },
    handleBurgerModalUpdate(config) {
      this.$refs['burger-modal-modal'].close()
      this.$emit('burger-update', config)
    },
    handleBurgerModalDelete(config) {
      this.$refs['burger-modal-modal'].close()
      this.$emit('burger-delete', config)
    },
    openBurgerModal(config) {
      const standardConfig =
        this.menu.burgers.find(burger => burger.id === config.id) ||
        this.menu.burger_bowls.find(burgerBowl => burgerBowl.id === config.id)
      this.currentBurgerModalStandardConfig = standardConfig
      this.currentBurgerModalEditConfig = config
    },
    resetBurgerModal() {
      this.currentBurgerModalStandardConfig = null
      this.currentBurgerModalEditConfig = null
    },
    handleBurgerModalClosed() {
      this.resetBurgerModal()
    },
    formatPrice
  }
}
</script>

<style lang="scss">
@import '~@/halifax-ui/src/scss/framework';

.cart {
  $module: '.cart';
  $jagged-edge-size: 3px;

  overflow: hidden;
  padding: $jagged-edge-size 0;

  &::before,
  &::after {
    content: '';
    display: block;
    height: $jagged-edge-size;
    width: 100%;
    background: linear-gradient(135deg, #fff 25%, transparent 25%) -50px 0,
      linear-gradient(225deg, #fff 25%, transparent 25%) -50px 0,
      linear-gradient(315deg, #fff 25%, transparent 25%),
      linear-gradient(45deg, #fff 25%, transparent 25%);
    background-size: $jagged-edge-size * 2 $jagged-edge-size * 2;
  }
  &::before {
    margin-top: -$jagged-edge-size;
    background-position: 0 $jagged-edge-size;
  }
  &::after {
    margin-bottom: -$jagged-edge-size;
  }
  &__collapse {
    border-bottom: 2px dotted #fff;

    // &:first-of-type {
    //   border-top: 2px dotted #fff;
    // }
  }
  &__item-label {
    background: #fff;
    height: 50px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 15px;
    font-weight: 700;
    padding-top: 5px;

    > span:last-of-type {
      display: flex;
      align-items: center;
      font-size: 1.3rem;
      color: darken(desaturate($background-color, 20%), 5%);
      margin-right: -5px;
    }

    &:active {
      background-color: darken(#fff, 3%);
    }
  }
  &__item-label-icon {
    width: 16.7px;
    height: auto;
    flex: 0 0 12.7px;
    transform: rotate(90deg);
    text-transform: none;
    transition: transform 250ms;
    margin-left: 6px;

    .collapsed & {
      transform: rotate(-90deg);
    }
  }
  &__item-label-expand {
    display: none;

    .collapsed & {
      display: block;
    }
  }
  &__item-label-collapse {
    .collapsed & {
      display: none;
    }
  }
  &__item {
    overflow: hidden;
    max-height: 96px;

    &--leave-active {
      transition: max-height 400ms;
    }
    &--leave-to {
      max-height: 0;
    }
  }
  &__heading {
    font-size: 1.6rem;

    &::first-letter {
      text-transform: capitalize;
    }
  }
  &__footer {
    background-color: #fff;
    padding: 16px 15px;
    text-align: right;
    font-weight: 700;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
